import { AxiosProgressEvent } from 'axios';

import { authorizedAxiosInstance } from '@/clients';

export interface CloudAttachment {
    createdTime?: string;
    iconLink?: string;
    id: string;
    kind?: 'file' | 'folder';
    mimeType: string;
    name: string;
    relativePath?: string;
    size?: string;
    webViewLink?: string;
}

export interface AttachmentDestination {
    listName: string;
    recordName: string;
    resourceId: string;
    resourceName: string;
    resourceType: 'investment' | 'list' | 'task';
}

export interface CloudStorageConnection {
    createdAt?: string;
    id: string;
    label?: string;
    type: string;
}

export interface DocuSignConnection {
    createdAt: string;
    id: string;
    label: string;
    type: 'DocuSign';
}

export interface DocuSignSenderView {
    accountName?: string;
    envelopeId: string;
    url: string;
}

export interface DocuSignSignature {
    font?: string;
    id: string;
    initials?: string;
    isDefault: boolean;
    name: string;
    previewDataUrl?: string;
}

interface UploadAttachmentInput {
    connection: CloudStorageConnection;
    destination: AttachmentDestination;
    file: File;
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    signal?: AbortSignal;
    uploadId: string;
}

interface AttachmentContentInput {
    attachment: CloudAttachment;
    connection: CloudStorageConnection;
    destination?: AttachmentDestination;
    signal?: AbortSignal;
}

const cloudStorageCapabilitiesByType: Record<string, { maxFileSizeBytes: number; supportsFolders: boolean }> = {
    Box: { maxFileSizeBytes: 50 * 1024 * 1024, supportsFolders: true },
    Dropbox: { maxFileSizeBytes: 100 * 1024 * 1024, supportsFolders: true },
    'Google Drive': { maxFileSizeBytes: 100 * 1024 * 1024, supportsFolders: true },
};
const CLOUD_STORAGE_CONNECTION_CACHE_TTL_MS = 30_000;
let cloudStorageConnectionsCache: { connections: CloudStorageConnection[]; expiresAt: number } | undefined;
let cloudStorageConnectionsRequest: Promise<CloudStorageConnection[]> | undefined;

export const invalidateCloudStorageConnectionsCache = () => {
    cloudStorageConnectionsCache = undefined;
    cloudStorageConnectionsRequest = undefined;
};

export const isAttachmentCloudStorageConnection = (connection: { type?: string }) =>
    Boolean(connection.type && cloudStorageCapabilitiesByType[connection.type]);

export const getCloudStorageCapabilities = (connection: CloudStorageConnection) =>
    cloudStorageCapabilitiesByType[connection.type] ?? {
        maxFileSizeBytes: 0,
        supportsFolders: false,
    };

export const getCloudStorageConnections = async ({ force = false }: { force?: boolean } = {}) => {
    if (!force && cloudStorageConnectionsCache && cloudStorageConnectionsCache.expiresAt > Date.now()) {
        return cloudStorageConnectionsCache.connections;
    }

    if (!force && cloudStorageConnectionsRequest) {
        return cloudStorageConnectionsRequest;
    }

    let request: Promise<CloudStorageConnection[]>;

    request = authorizedAxiosInstance
        .get<CloudStorageConnection[]>('/api/connected-accounts/cloud-storage')
        .then((response) => {
            const connections = response.data.filter(isAttachmentCloudStorageConnection);

            cloudStorageConnectionsCache = {
                connections,
                expiresAt: Date.now() + CLOUD_STORAGE_CONNECTION_CACHE_TTL_MS,
            };

            return connections;
        })
        .finally(() => {
            if (cloudStorageConnectionsRequest === request) {
                cloudStorageConnectionsRequest = undefined;
            }
        });

    if (!force) {
        cloudStorageConnectionsRequest = request;
    }

    return request;
};

export const getDocuSignConnections = async () => {
    const response = await authorizedAxiosInstance.get<DocuSignConnection[]>(
        '/api/auth/account/docusign/connected-accounts',
    );

    return response.data;
};

export const getDocuSignOAuthUrl = async () => {
    const response = await authorizedAxiosInstance.get<{ url: string }>('/api/auth/account/docusign/url');

    return response.data.url;
};

export const getDocuSignSignatures = async (connection: DocuSignConnection) => {
    const response = await authorizedAxiosInstance.get<DocuSignSignature[]>(
        `/api/auth/account/docusign/connected-accounts/${connection.id}/signatures`,
    );

    return response.data;
};

export const createDocuSignSenderView = async (
    docuSignConnection: DocuSignConnection,
    cloudStorageConnection: CloudStorageConnection,
    destination: AttachmentDestination,
    attachment: CloudAttachment,
    returnPath: string,
) => {
    const response = await authorizedAxiosInstance.post<DocuSignSenderView>(
        `/api/auth/account/docusign/connected-accounts/${docuSignConnection.id}/sender-view`,
        {
            attachmentId: attachment.id,
            cloudStorageConnectedAccountId: cloudStorageConnection.id,
            listName: destination.listName,
            recordName: destination.recordName,
            returnPath,
        },
        { params: getAttachmentResourceParams(destination) },
    );

    return response.data;
};

const getAttachmentResourceParams = (destination: AttachmentDestination) => ({
    resourceId: destination.resourceId,
    resourceName: destination.resourceName,
    resourceType: destination.resourceType,
});

export const getCloudAttachments = async (connection: CloudStorageConnection, destination: AttachmentDestination) => {
    const response = await authorizedAxiosInstance.get<CloudAttachment[]>(
        `/api/auth/account/cloud-storage/connected-accounts/${connection.id}/attachments`,
        {
            params: {
                listName: destination.listName,
                recordName: destination.recordName,
                ...getAttachmentResourceParams(destination),
            },
        },
    );

    return response.data;
};

export const getConnectedCloudFile = async (connectionId: string, fileId: string, signal?: AbortSignal) => {
    const response = await authorizedAxiosInstance.get<{
        attachment: CloudAttachment;
        connection: CloudStorageConnection;
    }>(
        `/api/auth/account/cloud-storage/connected-accounts/${encodeURIComponent(connectionId)}/files/${encodeURIComponent(fileId)}`,
        { signal, timeout: 30_000 },
    );
    return response.data;
};

export const getCloudAttachmentContent = async ({
    attachment,
    connection,
    destination,
    signal,
}: AttachmentContentInput) => {
    const response = await authorizedAxiosInstance.get<Blob>(
        `/api/auth/account/cloud-storage/connected-accounts/${connection.id}/${destination ? 'attachments' : 'files'}/${encodeURIComponent(attachment.id)}/content`,
        {
            params: destination
                ? {
                      listName: destination.listName,
                      recordName: destination.recordName,
                      ...getAttachmentResourceParams(destination),
                  }
                : undefined,
            responseType: 'blob',
            signal,
            timeout: 30_000,
        },
    );

    return response.data;
};

export const downloadCloudAttachments = async (
    connection: CloudStorageConnection,
    destination: AttachmentDestination,
    attachmentIds: string[],
) => {
    const response = await authorizedAxiosInstance.post<Blob>(
        `/api/auth/account/cloud-storage/connected-accounts/${connection.id}/attachments/download`,
        {
            attachmentIds,
            listName: destination.listName,
            recordName: destination.recordName,
        },
        { params: getAttachmentResourceParams(destination), responseType: 'blob' },
    );

    return response.data;
};

export const uploadCloudAttachment = async ({
    connection,
    destination,
    file,
    onUploadProgress,
    signal,
    uploadId,
}: UploadAttachmentInput) => {
    const formData = new FormData();

    formData.append('file', file);
    formData.append('listName', destination.listName);
    formData.append('recordName', destination.recordName);
    formData.append('relativePath', file.webkitRelativePath ?? '');
    formData.append('uploadId', uploadId);

    const response = await authorizedAxiosInstance.post<{ file: CloudAttachment; success: boolean }>(
        `/api/auth/account/cloud-storage/connected-accounts/${connection.id}/attachments`,
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
            params: getAttachmentResourceParams(destination),
            signal,
        },
    );

    return response.data.file;
};

export const createCloudAttachmentFolder = async (
    connection: CloudStorageConnection,
    destination: AttachmentDestination,
    folderName: string,
) => {
    const response = await authorizedAxiosInstance.post<{ folder: CloudAttachment; success: boolean }>(
        `/api/auth/account/cloud-storage/connected-accounts/${connection.id}/attachments/folders`,
        {
            folderName,
            listName: destination.listName,
            recordName: destination.recordName,
        },
        { params: getAttachmentResourceParams(destination) },
    );

    return response.data.folder;
};

export const moveCloudAttachment = async (
    connection: CloudStorageConnection,
    destination: AttachmentDestination,
    sourceAttachmentId: string,
    destinationFolderId?: string,
) => {
    const response = await authorizedAxiosInstance.patch<{ attachment: CloudAttachment; success: boolean }>(
        `/api/auth/account/cloud-storage/connected-accounts/${connection.id}/attachments/move`,
        {
            destinationFolderId: destinationFolderId ?? null,
            listName: destination.listName,
            recordName: destination.recordName,
            sourceAttachmentId,
        },
        { params: getAttachmentResourceParams(destination) },
    );

    return response.data.attachment;
};

export interface BulkAttachmentMoveResult {
    attachments: CloudAttachment[];
    failures: { attachmentId: string; message: string }[];
    success: boolean;
}

export const moveCloudAttachments = async (
    connection: CloudStorageConnection,
    destination: AttachmentDestination,
    sourceAttachmentIds: string[],
    destinationFolderId?: string,
) => {
    const response = await authorizedAxiosInstance.patch<BulkAttachmentMoveResult>(
        `/api/auth/account/cloud-storage/connected-accounts/${connection.id}/attachments/move-bulk`,
        {
            destinationFolderId: destinationFolderId ?? null,
            listName: destination.listName,
            recordName: destination.recordName,
            sourceAttachmentIds,
        },
        { params: getAttachmentResourceParams(destination) },
    );

    return response.data;
};

export const relocateCloudAttachments = (
    attachments: CloudAttachment[],
    sourceAttachmentIds: string[],
    destinationFolderId?: string,
) => {
    const sourceIds = new Set(sourceAttachmentIds);
    const selectedSources = attachments.filter((attachment) => sourceIds.has(attachment.id));
    const destinationFolder = destinationFolderId
        ? attachments.find((attachment) => attachment.id === destinationFolderId && attachment.kind === 'folder')
        : undefined;

    if (selectedSources.length !== sourceIds.size || (destinationFolderId && !destinationFolder)) {
        return attachments;
    }

    const destinationPath = destinationFolder?.relativePath;

    if (
        selectedSources.some((source) => {
            const sourcePath = source.relativePath ?? source.name;

            return (
                source.id === destinationFolder?.id ||
                destinationPath === sourcePath ||
                destinationPath?.startsWith(`${sourcePath}/`)
            );
        })
    ) {
        return attachments;
    }

    const sourceRoots = selectedSources.filter((source) => {
        const sourcePath = source.relativePath ?? source.name;

        return !selectedSources.some((candidate) => {
            const candidatePath = candidate.relativePath ?? candidate.name;

            return (
                candidate.kind === 'folder' && candidate.id !== source.id && sourcePath.startsWith(`${candidatePath}/`)
            );
        });
    });

    let changed = false;
    const relocated = attachments.map((attachment) => {
        const relativePath = attachment.relativePath ?? attachment.name;
        const source = sourceRoots.find((candidate) => {
            const sourcePath = candidate.relativePath ?? candidate.name;

            return (
                attachment.id === candidate.id ||
                (candidate.kind === 'folder' && relativePath.startsWith(`${sourcePath}/`))
            );
        });

        if (!source) {
            return attachment;
        }

        const sourcePath = source.relativePath ?? source.name;
        const movedSourcePath = [destinationPath, source.name].filter(Boolean).join('/');
        const nextRelativePath = `${movedSourcePath}${relativePath.slice(sourcePath.length)}`;

        if (nextRelativePath === relativePath) {
            return attachment;
        }

        changed = true;
        return { ...attachment, relativePath: nextRelativePath };
    });

    return changed ? relocated : attachments;
};

export const relocateCloudAttachmentFolder = (
    attachments: CloudAttachment[],
    sourceAttachmentId: string,
    destinationFolderId?: string,
) => {
    const source = attachments.find(
        (attachment) => attachment.id === sourceAttachmentId && attachment.kind === 'folder',
    );

    return source ? relocateCloudAttachments(attachments, [sourceAttachmentId], destinationFolderId) : attachments;
};
